import cx from 'classnames';
import { useRouter } from 'next/router';
import { useTranslation } from 'next-i18next';
import { type ComponentProps, useRef, useState } from 'react';
import { PopupContentFullscreen, usePopup } from '@dx-ui/osc-popup';

import { replaceLang } from '@dx-ui/framework-i18n';
import { Button, Heading, Link, Surface, Text } from '@dx-ui/global-ui-prism';
import { HeaderLogoLink } from '../header-logo';
import {
  sendGlobalNavLinkClickGoal,
  sendGlobalNavMenuOpenGoal,
  sendInteractionReward,
} from '../../header.utilities';

import { GlobalNavMenuProps, TDrawerItem } from '../../types';
import { HEADER_VARIANT_TYPE } from '../../header.constants';
import { useHeaderStateContext } from '../../context/header-state-context';

type NavigationMenuButtonProps = Pick<
  ComponentProps<typeof Button>,
  'fill' | 'color' | 'iconOnly' | 'underline' | 'textStyle' | 'iconProps' | 'label' | 'className'
>;

export const GlobalNavMenu: React.FC<GlobalNavMenuProps> = ({
  globalNavLinks = [],
  headerVariantType,
  surfaceType,
  isTranslucent,
}) => {
  const { t } = useTranslation('osc-header');
  const { locale } = useRouter();
  const firstLinkRef = useRef<HTMLAnchorElement>(null);
  const menuButtonRef = useRef<HTMLButtonElement | null>(null);
  const closeButtonRef = useRef<HTMLButtonElement | null>(null);
  const [isCloseClickable, setIsCloseClickable] = useState(false);

  const { closePopup, setOpen } = usePopup();
  const {
    styling: { logoVariant },
  } = useHeaderStateContext();

  const closeMainMenu = () => {
    if (isCloseClickable) {
      closePopup();
      setTimeout(() => {
        menuButtonRef.current?.focus();
      }, 100);
    }
  };

  const openMainMenu = () => {
    setOpen(true);
    setTimeout(() => {
      setIsCloseClickable(true);
    }, 100);
    sendGlobalNavMenuOpenGoal();
  };

  const handleLinkClick = (
    navItem: TDrawerItem | null | undefined,
    event: React.MouseEvent<HTMLAnchorElement>
  ) => {
    sendInteractionReward();
    sendGlobalNavLinkClickGoal();
    navItem?.link?.onClick?.(event);
  };

  const isReimaginedDesktopNav =
    headerVariantType === HEADER_VARIANT_TYPE.hamburgerNavMinimalReimagine;

  const menuOpenButtonBaseProps = {
    fill: 'flat',
    color: 'inherit',
    underline: false,
    iconProps: { name: 'menu', size: 'md' },
  } as NavigationMenuButtonProps;

  const menuOpenButtonProps = {
    ...menuOpenButtonBaseProps,
    ...(isReimaginedDesktopNav
      ? {
          iconOnly: false,
          textStyle: 'inline-text-style',
          iconProps: { ...menuOpenButtonBaseProps.iconProps, align: 'after', spacing: '1' },
          label: t('mainMenu'),
          className: cx('me-2 rounded-none', 'mb-1 !font-bold'),
        }
      : {
          iconOnly: true,
          textStyle: 'block-text-style',
          label: t('openMenu'),
          className: cx('me-2 rounded-none', 'w-12'),
        }),
  } as NavigationMenuButtonProps;

  return (
    <div
      className={cx('w-full', {
        flex:
          headerVariantType === HEADER_VARIANT_TYPE.hamburgerNavMinimal || isReimaginedDesktopNav,
      })}
    >
      <Button
        {...menuOpenButtonProps}
        id="menu-open-button"
        data-testid="hamburger-menu"
        ref={menuButtonRef}
        onClick={openMainMenu}
        aria-haspopup
      />
      {headerVariantType === HEADER_VARIANT_TYPE.hamburgerNavMinimal ? (
        <div className="flex flex-col items-start justify-center gap-2 px-2">
          <HeaderLogoLink
            variant={logoVariant}
            brand={{
              code: 'WW',
              url: '/',
              name: 'Hilton For the Stay',
            }}
          />
        </div>
      ) : null}

      <PopupContentFullscreen
        className={cx({
          'bg-blur': isTranslucent,
        })}
      >
        <Surface
          type={surfaceType || 'base'}
          className={cx('w-full', {
            '!bg-translucent': isTranslucent,
          })}
        >
          <Button
            fill="flat"
            color="inherit"
            iconOnly
            underline={false}
            iconProps={{ name: 'close' }}
            label={t('closeMenu')}
            id="nav-menu-close"
            onClick={() => {
              closeMainMenu();
            }}
            ref={closeButtonRef}
            className="ms-12 mt-4 !inline-flex rounded-none"
          />
          <div className="container-fluid">
            <div className="px-2 pb-4 pt-8">
              <div className="flex items-center justify-start">
                {headerVariantType === HEADER_VARIANT_TYPE.hamburgerNavMinimal ? (
                  <HeaderLogoLink
                    anchorClassName="mb-6 mt-2"
                    variant={logoVariant}
                    brand={{
                      code: 'WW',
                      url: '/',
                      name: 'Hilton For the Stay',
                    }}
                  />
                ) : (
                  <div className="size-20">
                    <Heading level={2} size="2xl">
                      <HeaderLogoLink
                        variant={logoVariant}
                        brand={{
                          code: 'WW',
                          url: '/',
                          name: 'Hilton For the Stay',
                        }}
                      />
                    </Heading>
                  </div>
                )}
              </div>
            </div>
            {globalNavLinks.length > 0 ? (
              <div className="grid grid-cols-4 gap-x-8 gap-y-6 px-2 pb-6">
                {globalNavLinks.map((navItem, index) => (
                  <div key={`nav-section-${navItem.label || index}`} className="flex flex-col pe-6">
                    <Heading level={3} size="sm">
                      {navItem.link && navItem?.subMenu?.length === 0 ? (
                        <Link
                          colorTheme="inherit"
                          ref={index === 0 ? firstLinkRef : undefined}
                          url={replaceLang(navItem.link.url, locale ?? 'en')}
                          anchorClassName="osc-nav-drawer-link block w-full px-3 py-3.5"
                          className="osc-nav-main-link text--md font-bold"
                          onClick={(event) => handleLinkClick(navItem, event)}
                          target={navItem.link.isNewWindow ? '_blank' : undefined}
                          showNewWindowIcon={navItem.link.isNewWindow}
                          underline={false}
                        >
                          {navItem.label}
                        </Link>
                      ) : (
                        <span className="osc-nav-main-link text--md flex h-full flex-1 items-center self-stretch px-3 pb-3.5 pt-5 font-bold no-underline">
                          {navItem.label}
                        </span>
                      )}
                    </Heading>
                    {navItem.subMenu && navItem.subMenu.length > 0 && (
                      <ul className="space-y-0">
                        {navItem.subMenu.map((subItem, subIndex) => {
                          if (!subItem?.link?.url) return null;
                          return (
                            <li key={`submenu-${navItem.label}-${subItem.label || subIndex}`}>
                              <span
                                className={cx(
                                  'block w-full border-b border-surface hover:font-bold',
                                  isTranslucent && 'border-b-border'
                                )}
                              >
                                <Link
                                  colorTheme="inherit"
                                  url={replaceLang(subItem.link.url, locale ?? 'en')}
                                  anchorClassName="osc-nav-drawer-link px-3 py-4 min-w-fit brand-ey:focus:font-normal block"
                                  className="osc-nav-drawer-link-text no-underline"
                                  onClick={(event) => handleLinkClick(subItem, event)}
                                  target={subItem.link.isNewWindow ? '_blank' : undefined}
                                  showNewWindowIcon={subItem.link.isNewWindow}
                                >
                                  {subItem.label}
                                </Link>
                              </span>
                            </li>
                          );
                        })}
                      </ul>
                    )}
                  </div>
                ))}
              </div>
            ) : (
              <Text color="muted" className="p-6 text-center">
                {t('noNavigationLinks', 'No navigation links available')}
              </Text>
            )}
          </div>
        </Surface>
      </PopupContentFullscreen>
    </div>
  );
};
